import _ from "lodash";
import React, {
  useState,
} from "react";
import {
  renderChildren,
  withLocation,
} from "CommonCore/router-util";
import ReactSpinner from "react-spinkit";
import {
  Badge,
  Button,
  Card,
  Collapse,
  Form,
} from "react-bootstrap";
import RcTooltip from "rc-tooltip";
import "rc-tooltip/assets/bootstrap.css"; // CSS nicht vergessen!
import DatePicker from "react-datepicker";
import moment from "moment";
import numeral from "numeral";

import {
  defaultFromSchema,
  injectGenerator,
} from "CommonCore/schema-util";
import {
  LocaleDateFormat,
  LocaleDateFormatShort,
  LocaleDateFormatNumber,
  LocaleDateFormatNumberShort,
  LocaleMonthFormat,
  IsoDateFormat,
  LocaleNumberFormat,
  formatNumber,
  formatMoney,
  LowpNumberFormat,
} from "CommonCore/l10n";
import {
  MarkdownPreview,
} from "CommonComponents/markdown-editor";
import {
  DetailEditor,
  DetailView,
} from "./detail-view";
import {
  v4 as uuidv4,
} from "uuid";

export const Spinner = () => <ReactSpinner fadeIn='none' style={{ display: "inline-block" }} />;

export const Pill = ({ children, variant, className }) => <Badge variant={variant} bg={null} className={`label-pill ${ className || "" } badge-${ variant }`}>{children}</Badge>;

export const Tooltip = ({ overlay, placement, children, visible }) => <RcTooltip
  placement={placement || "top"}
  mouseLeaveDelay={0}
  showArrow={true}
  {... (_.isUndefined(visible) ? {} : { visible })}
  styles={{
    body: {
      backgroundColor: "#f8f9fa",
      color: "#000",
    },
    arrow: {
      backgroundColor: "transparent",
      borderColor: "transparent",
    },
  }}
  overlay={<div>{overlay}</div>}>
  <span>{children}</span>
</RcTooltip>;
export class DelayedTooltip extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showing: false,
    };
    this.timeoutToken = null;
  }

  componentDidMount() {
    this.update(this.props);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.overlay !== this.props.overlay ||
        prevProps.condition !== this.props.condition) {
      this.update(this.props);
    }
  }

  componentWillUnmount() {
    if (this.timeoutToken) {
      clearTimeout(this.timeoutToken);
      this.timeoutToken = null;
    }
  }

  update(props) {
    const shouldShow = props.condition && props.overlay;

    if (shouldShow && !this.state.showing && !this.timeoutToken) {
      // Tooltip soll gezeigt werden, ist aber noch nicht sichtbar und kein Timer läuft
      this.timeoutToken = setTimeout(() => {
        this.setState({ showing: true });
        this.timeoutToken = null;
      }, props.delay || 0);
    } else if (!shouldShow) {
      // Tooltip soll nicht gezeigt werden
      if (this.state.showing) {
        this.setState({ showing: false });
      }
      if (this.timeoutToken) {
        clearTimeout(this.timeoutToken);
        this.timeoutToken = null;
      }
    }
  }

  render() {
    const { overlay } = this.props;
    const { showing } = this.state;

    return (
      <Tooltip visible={showing} overlay={overlay}>
        {this.props.children}
      </Tooltip>
    );
  }
}

export const SchemaHeader = ({ useHeader, header, display, tooltip, hideLabel, entity }) => {
  if (hideLabel) {
    return null;
  }
  const text = useHeader && header ? header : (_.isFunction(display) ? display(entity) : display);
  return tooltip ? <Tooltip overlay={tooltip}>{text}</Tooltip> : <span>{text}</span>;
};

class CustomDateInput extends React.Component {
  render() {
    const { template, labelClassName, formatedDate, onClick } = this.props;
    return <a name='dateInput' className={labelClassName || ""} onClick={onClick}>{template({ date: formatedDate })}</a>;
  }
}

export class CustomDateField extends React.Component {
  constructor() {
    super();
    this.state = {};
  }

  render() {
    const { timestamp, template, labelClassName } = this.props;
    const localeDate = moment(timestamp).toDate();
    const formatedDate = moment(timestamp).format(LocaleDateFormat);

    return <DatePicker
      dateFormat={LocaleDateFormat.replace(/D/g, "d").replace(/Y/g, "y")}
      showMonthDropdown
      showYearDropdown
      showWeekNumbers
      onChange={this.onChange}
      selected={localeDate}
      customInput={<CustomDateInput formatedDate={formatedDate} template={template} labelClassName={labelClassName} />}
    />;
  }

  onChange = text => {
    this.props.onChange(moment(text, LocaleDateFormat).format(IsoDateFormat));
    this.togglePicker();
  };

  togglePicker = () => {
    this.setState({ isOpen: !this.state.isOpen });
  };
}

export class CustomDatePicker extends React.Component {
  constructor(props) {
    super(props);
    const { value } = props;
    this.state = {
      fieldValue: value ? moment(value, IsoDateFormat).toDate() : null,
      sourceValue: value,
    };
  }

  componentDidUpdate(prevProps) {
    const { value } = this.props;

    // KI claud.ai: Nicht überschreiben, wenn der User gerade eine Änderung gemacht hat
    if (this.state.userChanged) {
      this.setState({ userChanged: false });
      return;
    }

    if (value !== prevProps.value && value !== this.state.sourceValue) {
      this.setState({
        fieldValue: value ? moment(value, IsoDateFormat).toDate() : null,
        sourceValue: value,
      });
    }
  }

  render() {
    const { fieldValue } = this.state;
    const { disabled, isClearable, name, isMonthPicker } = this.props;
    return <DatePicker
      name={name}
      dateFormat={(isMonthPicker ? LocaleMonthFormat : LocaleDateFormat).replace(/D/g, "d").replace(/Y/g, "y")}
      showMonthYearPicker={isMonthPicker}
      showMonthDropdown
      showYearDropdown
      showWeekNumbers
      fixedHeight
      disabled={disabled}
      isClearable={isClearable}
      className='form-control'
      selected={fieldValue}
      onChange={this.onChange}
      onChangeRaw={this.onChangeRaw}
      onBlur={this.onBlur}
      strictParsing
    />;
  }

  onChange = value => {
    const isoValue = value ? moment(value).format(IsoDateFormat) : null;
    this.setState({
      fieldValue: value,
      sourceValue: isoValue,
      // KI claud.ai: Flag setzen, dass User die Änderung gemacht hat
      userChanged: true,
    });
    this.props.onChange(isoValue);
  };

  onBlur = ({ target: { value } }) => {
    const { isMonthPicker } = this.props;

    // Überspringe Validierung für MonthPicker, da das Format anders ist
    if (isMonthPicker) {
      return;
    }

    if (!this.getValidDate(value)) {
      this.setState({ fieldValue: null });
      this.props.onChange(null);
    }
  };

  onChangeRaw = ({ target: { value } }) => {
    const result = this.getValidDate(value);
    if (result) {
      this.setState({ fieldValue: result.toDate() });
      this.props.onChange(result.format(IsoDateFormat));
    }
  };

  getValidDate(value) {
    const { isMonthPicker } = this.props;
    if (isMonthPicker) {
      const parseMonth = moment(value, LocaleMonthFormat, true);
      return parseMonth.isValid() ? parseMonth : null;
    }

    const parse6 = moment(value, LocaleDateFormatNumberShort, true);
    const parse8 = moment(value, LocaleDateFormatNumber, true);
    const parseShort = moment(value, LocaleDateFormatShort, true);
    const parseLong = moment(value, LocaleDateFormat, true);
    return parse6.isValid()
? parse6
: parse8.isValid()
? parse8
        : parseShort.isValid() ? parseShort : parseLong.isValid() ? parseLong : null;
  }
}

export function Awesome({ name, className, style }) {
  return <i className={`fa fa-${ name } ${ className || "" }`} style={style} aria-hidden='true' />;
}

export function formatValue(entity, schema, renderMethods, injects) {
  let value = entity[schema.field];
  let renderMethod = renderMethods && renderMethods[schema.field];
  if (renderMethod) {
    if (renderMethod.generator) {
      renderMethod = injectGenerator(renderMethod.generator, renderMethod.inject, injects);
    }
    value = renderMethod(value, entity);
  } else if (schema.selector) {
    const gen = injectGenerator(schema.selector, schema.inject, injects);
    value = gen(value, entity);
  } else if (schema.type === "date") {
    value = value && (value.indexOf("+") !== -1 || value.endsWith("Z") ? moment : moment.utc)(value).format(schema.format || LocaleDateFormat);
  } else if (schema.type === "month") {
    value = value && (value.indexOf("+") !== -1 || value.endsWith("Z") ? moment : moment.utc)(value).format(schema.format || LocaleMonthFormat);
  } else if (schema.type === "number" && _.isNumber(value)) {
    value = numeral(value).format(schema.numeralFormat || LocaleNumberFormat);
    if (schema.format) {
      value = schema.format(value);
    }
  } else if (schema.type === "checkbox") {
    value = _.isBoolean(value)
      ? <Pill variant={value ? "success" : "default"}>{value ? "Ja" : "Nein"}</Pill>
      : undefined;
  } else if (schema.type === "money") {
    value = value ? formatMoney(value) : "";
  } else if (schema.type === "markdown") {
    value = <MarkdownPreview value={value} />;
  }
  return value;
}

@withLocation
export class GoToPageButton extends React.Component {
  render() {
    return <Button variant={this.props.variant || "outline-primary"} onClick={this.navigate}>
      {renderChildren(this.props)}
    </Button>;
  }

  navigate = () => {
    const { history, to } = this.props;
    history.push(to);
  };
}

export function makeChartOptions({ money } = {}) {
  const formatAmount = (value, precise) => money
    ? formatMoney(value, precise ? undefined : "0,0 €")
    : formatNumber(value, LowpNumberFormat);
  return {
    legend: {
      bottomPadding: 16,
      labels: {
        fontSize: 13,
      },
    },
    title: {
      fontSize: 16,
    },
    scales: {
      yAxes: [{
        ticks: {
          beginAtZero: true,
          callback: value => formatAmount(value, false),
        },
      }],
    },
    spanGaps: true,
    tooltips: {
      bodyFontSize: 16,
      titleFontSize: 16,
      footerFontSize: 16,
      callbacks: {
        title: (items, data) => {
          if (data.labelsLong) {
            return data.labelsLong[items[0].xLabel];
          }
        },
        label: (tooltipItem, data) => {
          const datasetLabel = data.datasets[tooltipItem.datasetIndex].label;
          return datasetLabel + ": " + formatAmount(tooltipItem.yLabel, true);
        },
      },
    },
  };
}

export class EditPanel extends React.Component {
  constructor() {
    super();
    this.state = {
      editMode: false,
      edits: {},
    };
  }

  get entity() {
    const {
      injects,
    } = this.props;
    const entity = _.cloneDeep(this.props.entity);

    const entityWithEdits = _.assign({}, entity, this.state.edits);
    const defaults = defaultFromSchema(this.props.schema, injects, entityWithEdits);

    return _.assign(defaults, entityWithEdits);
  }

  componentDidMount() {
    if (this.props.defaultEditable) {
      this.setState({ editMode: true });
    }
  }

  render() {
    const {
      validationMessage,
      validationErrors,
    } = this.state;
    const {
      schema,
      injects,
      entity,
      customControls,
      renderMethods,
      toolbarComponents,
      toolbarComponentsReadonly,
    } = this.props;

    const headerContent = [];
    if (this.props.header) {
      headerContent.push(this.props.header);
    }
    if (!this.props.unshowSatus && this.props.editable && !this.state.editMode) {
      headerContent.push(<span key='actions1'>
        <div key='actions' className='actions'>
          {toolbarComponents}
          <Button
            variant={this.props.action && this.props.action.buttonUpStyle || "outline-primary"}
            onClick={this.toggleEditMode}
          >
            {this.props.action && this.props.action.buttonUpName || "Ändern"}
          </Button>
        </div>
      </span>);
    }
    if (!this.props.editable && toolbarComponentsReadonly) {
      headerContent.push(<div key='actions' className='actions'>{toolbarComponentsReadonly}</div>);
    }

    return <div className={`box-panel ${ this.props.action && this.props.action.scrollable ? "scrollable" : "" } ${ this.props.action && this.props.action.extraClass || "" }`}>
      <Panel
        header={headerContent.length > 0 ? headerContent : null}
        variant={this.props.variant || void 0}
      >
        {renderChildren(this.props)}
        {!this.props.unshowSatus && <div>
          {this.state.editMode ?
            <DetailEditor
              schema={schema}
              entity={this.entity}
              injects={_.assign(injects, { entity: this.entity })}
              validationMessage={validationMessage}
              validationErrors={validationErrors}
              customControls={customControls}
              onChange={this.onChange}
              onClose={!this.props.hideCancel ? this.toggleEditMode : null}
              onSave={!this.props.hideSubmit ? this.onSave : null}
              submitText={this.props.submitText}
              disableSubmit={this.props.disableSubmit}
            /> :
            <div onClick={this.onPanelClick}>
              {this.props.topwell && <Well>{this.props.topwell}</Well>}
              <DetailView
                schema={schema}
                entity={entity}
                injects={injects}
                renderMethods={renderMethods}
                customControls={customControls}
              />
              {this.props.bottomwell && <Well>{this.props.bottomwell}</Well>}
            </div>}
        </div>}
      </Panel>
    </div>;
  }

  onPanelClick = () => {
    if (this.props.editOnClick) {
      this.setState({ editMode: true });
    }
  };

  onChange = change => {
    const { edits } = this.state;
    _.assign(edits, change);
    this.props.onChange && this.props.onChange(change, edits);
    this.setState({ edits: edits });
    this.props.onUpdate && this.props.onUpdate(edits);
  };

  toggleEditMode = () => {
    let { edits } = this.state;
    this.state.editMode && this.setState({ validationMessage: undefined, validationErrors: undefined });
    this.props.onClose && this.props.onClose();
    edits = {};

    if (!this.state.editMode) {
      // Bei Wechsel in Editmode z.B. defaults als Edits erzeugen
      this.props.onBeginEdit && this.props.onBeginEdit(edits, this.entity);
    }
    this.setState({ editMode: !this.state.editMode, edits: edits });
  };

  onSave = async() => {
    try {
      await this.props.onSave(this.entity, this.toggleEditMode);
      this.setState({ validationMessage: undefined, validationErrors: undefined });
      this.setState({ validationMessage: undefined, validationErrors: undefined });
    } catch(e) {
      // Falls der Fehler nicht silent ist, Meldung weitergeben
      if (!e.silent) {
        const response = e.response?.nachricht ? e.response : { nachricht: "Es ist ein Fehler aufgetreten." };
        this.setState({
          validationMessage: response.nachricht,
          validationErrors: response.details,
        });
      }
      return;
    }
    if (!this.props.noToggleOnSave) {
      this.toggleEditMode();
    }
    this.props.afterCommit && this.props.afterCommit();
  };
}

export class EditPanelTable extends React.Component {
  constructor() {
    super();
    this.state = {
      editMode: false,
      edits: {},
    };
  }

  get entity() {
    const {
      injects,
    } = this.props;
    const entity = _.cloneDeep(this.props.entity);

    const entityWithEdits = _.assign({}, entity, this.state.edits);
    const defaults = defaultFromSchema(this.props.schema, injects, entityWithEdits);

    return _.assign(defaults, entityWithEdits);
  }

  render() {
    const {
      validationMessage,
      validationErrors,
    } = this.state;
    const {
      customControls,
    } = this.props;

    return <div className='box-panel'>
      <Panel
        header={[
          this.props.header,
          this.props.editable && !this.state.editMode && <div key='actions' className='actions'>
            <Button
              variant={this.props.action && this.props.action.buttonUpStyle || "outline-primary"}
              onClick={this.onClose}
            >
              {this.props.action && this.props.action.buttonUpName || "Ändern"}
            </Button>
          </div>,
        ]}
        variant={this.props.variant || void 0}
      >
        <DetailEditor
          schema={this.props.schema}
          entity={this.entity}
          injects={this.props.injects}
          validationMessage={validationMessage}
          validationErrors={validationErrors}
          customControls={customControls}
          onChange={this.onChange}
          onClose={this.onClose}
          onSave={this.onSave}
          submitText={this.props.submitText}
        />
      </Panel>
    </div>;
  }

  onChange = change => {
    const { edits } = this.state;
    _.assign(edits, change);
    this.setState({ edits: edits });
  };

  onSave = async() => {
    try {
      await this.props.onSave(this.entity);
      this.setState({ validationMessage: undefined, validationErrors: undefined });
    } catch(e) {
      const response = e.response || { nachricht: "Es ist ein Fehler aufgetreten." };
      this.setState({
        validationMessage: response.nachricht,
        validationErrors: response.details,
      });
      return;
    }

    this.onClose();
    this.props.afterCommit && this.props.afterCommit();
  };

  onClose = () => {
    this.props.onClose(this.props.dataId);
  };
}

// Bootstrap 3 to 4 Wrappers
export const Panel = props => {
  const [open, setOpen] = useState(props.defaultExpanded);
  return <Card {..._.omit(props, "header", "collapsible", "defaultExpanded", "children")}>
    {props.header && <Card.Header>
      {props.collapsible
        ? <a onClick={() => setOpen(!open)} aria-expanded={open} aria-controls='example-collapse-text'>
          {props.header}
        </a>
        : props.header
      }

    </Card.Header>}
    {props.collapsible ?
      <Collapse in={open}>
        <Card.Body id='example-collapse-text'>
          {renderChildren(props)}
        </Card.Body>
      </Collapse> :
      <Card.Body>
        {renderChildren(props)}
      </Card.Body>
    }
  </Card>;
};

export const Checkbox = props => {
  const name = props.name || uuidv4();
  const labelOrId = _.isString(props.label) ? props.label : props.id;
  const parentSuffix = props.parentIndex !== undefined ? `-${ props.parentIndex }` : "";
  const controlId = `${ labelOrId }${ parentSuffix }-${ name }`;

  return (
    <Form.Group
      className={props.vertical ? "vertical-group" : "inline-group"}
      controlId={controlId}
    >
      <Form.Check
        className='form-check'
        type='checkbox'
        {..._.omit(props, "vertical", "parentIndex")}
      />
    </Form.Group>
  );
};

export const Radio = props => {
  const controlId = `${ _.isString(props.label) ? props.label : props.id }${ props.parentIndex !== undefined ? ("-" + props.parentIndex) : "" }-${ props.name }`;
  return <Form.Group className={props.vertical ? "vertical-group" : "inline-group"} controlId={controlId}>
    <Form.Check type='radio' {..._.omit(props, "vertical", "parentIndex")} />
  </Form.Group>;
};

export const Well = props => {
  return <Card {..._.omit(props, "header", "collapsible", "defaultExpanded", "children")}>
    <Card.Header className='well'>
      {renderChildren(props)}
    </Card.Header>
  </Card>;
};
